import { Box, Grid, Skeleton, Typography } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import Icon from '@apigrowfinance/pbolt-icon';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { RiAccountCircleLine } from 'react-icons/ri';

import { TransactionItem } from '@clients/transactionsClient';
import getEnumTranslation from '@utils/getEnumTranslation';

interface TransactionDetailsHeaderProps {
    transaction: TransactionItem;
    isLoading: boolean;
}

const TransactionDetailsHeader = ({
    transaction,
    isLoading,
}: TransactionDetailsHeaderProps) => {
    const { theme } = useTheme();
    const headerAmountObjectDisplay = transaction?.amount?.find(
        (amountObject) => amountObject.type === 'PRINCIPAL',
    );

    const isCreditingAccount = transaction?.transaction_type?.credit;

    return (
        <Grid container item justifyContent="space-between">
            <Grid item display="flex" xs={12} sm={6}>
                <Box sx={{ margin: 2.25 }}>
                    <Icon data-testid="transaction-logo">
                        <RiAccountCircleLine />
                    </Icon>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center">
                    {isLoading ? (
                        <Skeleton
                            width={theme.spacing(18)}
                            data-testid="merchant-name-loading"
                        />
                    ) : (
                        <Typography variant="h6" component="h2">
                            {transaction?.merchant?.name || transaction?.id}
                        </Typography>
                    )}
                    <Box component="span">
                        {isLoading ? (
                            <Skeleton
                                width={theme.spacing(18)}
                                data-testid="event-date-loading"
                            />
                        ) : (
                            <Typography variant="body2">
                                <DateFormatter
                                    date={
                                        transaction?.event_date ||
                                        transaction?.created_at
                                    }
                                    format="shortDateWithTimeArb"
                                />
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Grid>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                justifyContent="center"
                sx={{ ml: 2.25 }}>
                {headerAmountObjectDisplay &&
                    (isLoading ? (
                        <Skeleton
                            width={theme.spacing(18)}
                            data-testid="transaction-amount-loading"
                        />
                    ) : (
                        <Typography fontWeight={500}>
                            <CurrencyFormatter
                                testId="statement-balance"
                                amount={headerAmountObjectDisplay?.value}
                                currency={headerAmountObjectDisplay?.currency}
                                symbol={
                                    isCreditingAccount ? 'positive' : 'default'
                                }
                                variant={
                                    isCreditingAccount ? 'success' : 'default'
                                }
                            />
                        </Typography>
                    ))}
                {isLoading ? (
                    <Skeleton
                        width={theme.spacing(18)}
                        data-testid="transaction-type-loading"
                    />
                ) : (
                    transaction?.transaction_type && (
                        <Typography variant="body2">
                            {transaction.transaction_type?.credit
                                ? getEnumTranslation('credit')
                                : getEnumTranslation('debit')}
                        </Typography>
                    )
                )}
            </Box>
        </Grid>
    );
};

export default TransactionDetailsHeader;
